// Profile Panel - AgentBheem ERP (Simplified)
// Shows user info and sign out only
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/features/auth/store/authStore'
import {
  User,
  Mail,
  Building2,
  X,
  LogOut,
} from 'lucide-react'

interface ProfilePanelProps {
  isOpen: boolean
  onClose: () => void
}

// Format role for display (e.g., "super_admin" → "Super Admin")
function formatRole(role: string | undefined): string {
  if (!role) return 'User'
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

interface InfoRowProps {
  icon: React.ReactNode
  label: string
  value: string | undefined
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="flex-shrink-0 w-8 h-8 rounded bg-indigo-500/10 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900 truncate">
          {value || 'Not set'}
        </p>
      </div>
    </div>
  )
}

export function ProfilePanel({ isOpen, onClose }: ProfilePanelProps) {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  const handleLogout = () => {
    logout()
    onClose()
    navigate('/auth/login')
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed right-0 top-0 bottom-0 z-[9999] w-full max-w-sm',
          'bg-white border-l border-gray-200',
          'flex flex-col',
          'animate-slide-in-right shadow-xl'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-indigo-500" />
            <h2 className="font-semibold text-gray-900">My Profile</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Profile Card */}
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-6">
            {/* Avatar & Info */}
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30">
                <span className="text-2xl font-bold text-white">
                  {user?.full_name?.charAt(0)?.toUpperCase() ||
                    user?.username?.charAt(0)?.toUpperCase() ||
                    'U'}
                </span>
              </div>

              {/* Name & Role */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-white truncate">
                  {user?.full_name || user?.username || 'User'}
                </h3>
                <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-medium">
                  {formatRole(user?.role)}
                </span>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="rounded-xl p-4 bg-gray-50 border border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Account Information
            </h4>
            <div className="divide-y divide-gray-100">
              <InfoRow
                icon={<Mail className="h-4 w-4 text-indigo-500" />}
                label="Email"
                value={user?.email}
              />
              <InfoRow
                icon={<User className="h-4 w-4 text-indigo-500" />}
                label="Username"
                value={user?.username}
              />
              {/* Only show Company if we have a proper name (not UUID) */}
              {user?.company_name && (
                <InfoRow
                  icon={<Building2 className="h-4 w-4 text-indigo-500" />}
                  label="Company"
                  value={user.company_name}
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-3 rounded-xl',
              'bg-red-50 text-red-600 border border-red-100',
              'hover:bg-red-100 transition-all',
              'text-sm font-medium'
            )}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </div>
    </>
  )
}
